import { FILTERS } from '../configs/filters/filters';
import {
  FILTER_HTTP_PARAM_NAMES,
  FILTER_HTTP_VALUE_TRANSFORMS,
  SORT_HTTP_PARAM_NAMES,
} from '../configs/filters/filters';
import {
  ActiveSort,
  ActiveFilterValue,
  ActiveFilters,
  ActiveObjectFilter,
  CheckboxFilterType,
  FilterMenuConfig,
  FilterType,
  FilterTypeName,
  GetActiveFiltersOptions,
  RangeFilterType,
  FilterHandler,
  ACTIVE_SORT_HTTP_PARAM_NAMES,
  ActiveFilterHttpParam,
  CheckboxValueType,
  SearchFilterType,
  RadioFilterType,
} from '../types/filter.type';

const FILTER_HANDLERS: {
  [T in FilterTypeName]: FilterHandler<Extract<FilterType, { type: T }>>;
} = {
  range: {
    isEqual: (filter: RangeFilterType, defaultFilter: RangeFilterType) =>
      filter.min === defaultFilter.min && filter.max === defaultFilter.max,
    toActiveValue: (filter: RangeFilterType) => ({
      min: filter.min,
      max: filter.max,
    }),
  },
  checkbox: {
    isEqual: (filter: CheckboxFilterType, defaultFilter: CheckboxFilterType) =>
      areListsEqual(getActiveCheckboxLabels(filter), getActiveCheckboxLabels(defaultFilter)),
    toActiveValue: (filter: CheckboxFilterType) => {
      const labels: string[] = getActiveCheckboxLabels(filter);

      return labels.length > 0 ? labels : undefined;
    },
  },
  radio: {
    isEqual: (filter: RadioFilterType, defaultFilter: RadioFilterType) =>
      areListsEqual(getActiveCheckboxLabels(filter), getActiveCheckboxLabels(defaultFilter)),
    toActiveValue: (filter: RadioFilterType) => {
      const labels: string[] = getActiveCheckboxLabels(filter);

      return labels.length > 0 ? labels : undefined;
    },
  },
  search: {
    isEqual: (filter: SearchFilterType, defaultFilter: SearchFilterType) =>
      filter.value === defaultFilter.value,
    toActiveValue: (filter: SearchFilterType) => ({
      value: filter.value,
    }),
  },
};

export function getActiveFilters(
  filters: Record<string, FilterMenuConfig>,
  options: GetActiveFiltersOptions = {},
): ActiveFilters {
  const defaultFilters: Record<string, FilterMenuConfig> = options.defaultFilters ?? FILTERS;

  return getColumns(filters, options.tableHeaderElements).reduce(
    (activeFilters: ActiveFilters, column: string) => {
      return addColumnActiveFilters(activeFilters, column, filters[column], defaultFilters[column]);
    },
    {},
  );
}

export function getFiltersDifferentFromDefault(
  filters: Record<string, FilterMenuConfig>,
  defaultFiltersOrTableHeaderElements: Record<string, FilterMenuConfig> | string[] = FILTERS,
  tableHeaderElements?: string[],
): ActiveFilters {
  const defaultFilters: Record<string, FilterMenuConfig> = Array.isArray(
    defaultFiltersOrTableHeaderElements,
  )
    ? FILTERS
    : defaultFiltersOrTableHeaderElements;

  return getActiveFilters(filters, {
    defaultFilters,
    tableHeaderElements: Array.isArray(defaultFiltersOrTableHeaderElements)
      ? defaultFiltersOrTableHeaderElements
      : tableHeaderElements,
  });
}

export function getActiveFilterHttpParams(activeFilters: ActiveFilters): ActiveFilterHttpParam[] {
  return Object.entries(activeFilters).flatMap(
    ([filterName, value]: [string, ActiveFilterValue]) => {
      if (Array.isArray(value)) {
        const params: ActiveFilterHttpParam[] = value.map((item: string) => ({
          name: getActiveFilterParamName(filterName),
          value: item,
        }));

        return filterName === 'country'
          ? [
              ...params,
              {
                name: 'locationType',
                value: 'country',
              },
            ]
          : params;
      }

      return getActiveObjectFilterHttpParams(filterName, value);
    },
  );
}

export function getActiveSortHttpParams(
  activeSort: ActiveSort | undefined,
): ActiveFilterHttpParam[] {
  if (!activeSort) return [];

  return [
    {
      name: ACTIVE_SORT_HTTP_PARAM_NAMES.sortType,
      value: transformActiveSortTypeHttpValue(activeSort.sortType),
    },
    {
      name: ACTIVE_SORT_HTTP_PARAM_NAMES.sortDirection,
      value: activeSort.sortDirection,
    },
  ];
}

function getColumns(
  filters: Record<string, FilterMenuConfig>,
  tableHeaderElements: string[] | undefined,
): string[] {
  return tableHeaderElements ?? Object.keys(filters);
}

function addColumnActiveFilters(
  activeFilters: ActiveFilters,
  column: string,
  filterConfig: FilterMenuConfig | undefined,
  defaultFilterConfig: FilterMenuConfig | undefined,
): ActiveFilters {
  if (!filterConfig) return activeFilters;

  if (filterConfig.filters?.length > 0) {
    filterConfig.filters.forEach((filter: FilterType, index: number) => {
      const activeValue: ActiveFilterValue | undefined = getActiveValueForFilter(
        filter,
        defaultFilterConfig?.filters[index],
      );

      if (activeValue === undefined) return;

      const activeKey: string = filter.activeKey ?? column;
      const mergedValue: ActiveFilterValue | undefined = mergeActiveValues(
        [activeFilters[activeKey], activeValue].filter(
          (value): value is ActiveFilterValue => value !== undefined,
        ),
      );

      if (mergedValue !== undefined) {
        activeFilters[activeKey] = mergedValue;
      }
    });
  }

  return activeFilters;
}

function getActiveValueForFilter(
  filter: FilterType,
  defaultFilter: FilterType | undefined,
): ActiveFilterValue | undefined {
  const handler: FilterHandler<FilterType> = getFilterHandler(filter);

  if (defaultFilter?.type === filter.type && handler.isEqual(filter, defaultFilter)) {
    return undefined;
  }

  return handler.toActiveValue(filter);
}

function getFilterHandler<TFilter extends FilterType>(filter: TFilter): FilterHandler<TFilter> {
  return FILTER_HANDLERS[filter.type] as FilterHandler<TFilter>;
}

function mergeActiveValues(activeValues: ActiveFilterValue[]): ActiveFilterValue | undefined {
  const labels: string[] = getUniqueLabels(activeValues.filter(isStringList).flat());
  if (labels.length > 0) return labels;

  const objectValue: ActiveObjectFilter = activeValues.filter(isActiveObjectFilter).reduce(
    (mergedValue: ActiveObjectFilter, activeValue: ActiveObjectFilter) => ({
      ...mergedValue,
      ...activeValue,
    }),
    {},
  );

  return Object.keys(objectValue).length > 0 ? objectValue : undefined;
}

function isStringList(activeValue: ActiveFilterValue): activeValue is string[] {
  return Array.isArray(activeValue);
}

function isActiveObjectFilter(activeValue: ActiveFilterValue): activeValue is ActiveObjectFilter {
  return !Array.isArray(activeValue);
}

function getActiveCheckboxLabels(filter: CheckboxFilterType | RadioFilterType): string[] {
  return filter.data
    .filter((value: CheckboxValueType) => value.isActive === true)
    .map((data: CheckboxValueType) => data.value);
}

function getUniqueLabels(labels: string[]): string[] {
  return [...new Set(labels)];
}

function getActiveObjectFilterHttpParams(
  filterName: string,
  value: ActiveObjectFilter,
): ActiveFilterHttpParam[] {
  return Object.entries(value)
    .filter(([valueKey, valueItem]: [string, string | number]) => {
      return !isDefaultFilterHttpValue(filterName, valueKey, valueItem);
    })
    .map(([valueKey, valueItem]: [string, string | number]) => ({
      name: getActiveFilterParamName(filterName, valueKey),
      value: transformActiveFilterHttpValue(filterName, valueKey, valueItem),
    }));
}

function transformActiveSortTypeHttpValue(sortType: string): string {
  const sortTypeParamName: string | Record<string, string> | undefined =
    SORT_HTTP_PARAM_NAMES[sortType];

  return typeof sortTypeParamName === 'string' ? sortTypeParamName : sortType;
}

function getActiveFilterParamName(filterName: string, valueKey?: string): string {
  const paramNameConfig: string | Record<string, string> | undefined =
    FILTER_HTTP_PARAM_NAMES[filterName];

  if (typeof paramNameConfig === 'string') return paramNameConfig;
  if (valueKey && paramNameConfig?.[valueKey]) return paramNameConfig[valueKey];
  if (!valueKey) return filterName;

  return `${filterName}${valueKey.charAt(0).toUpperCase()}${valueKey.slice(1)}`;
}

function transformActiveFilterHttpValue(
  filterName: string,
  valueKey: string | undefined,
  value: string | number,
): string | number {
  const transform: ((value: string | number, valueKey?: string) => string | number) | undefined =
    FILTER_HTTP_VALUE_TRANSFORMS[filterName];

  return transform ? transform(value, valueKey) : value;
}

function isDefaultFilterHttpValue(
  filterName: string,
  valueKey: string,
  value: string | number,
): boolean {
  const defaultValue: string | number | undefined = getDefaultActiveObjectFilterValue(
    filterName,
    valueKey,
  );

  return defaultValue === value || undefined === value;
}

function getDefaultActiveObjectFilterValue(
  filterName: string,
  valueKey: string,
): string | number | undefined {
  const defaultActiveValue: ActiveFilterValue | undefined = addColumnActiveFilters(
    {},
    filterName,
    FILTERS[filterName],
    undefined,
  )[filterName];

  if (!defaultActiveValue || Array.isArray(defaultActiveValue)) return undefined;

  return defaultActiveValue[valueKey as keyof ActiveObjectFilter];
}

function areListsEqual(currentLabels: string[], defaultLabels: string[]): boolean {
  return (
    currentLabels.length === defaultLabels.length &&
    currentLabels.every((label: string, index: number) => label === defaultLabels[index])
  );
}
